import { useState, useMemo, useRef, useEffect } from "react";
import Map, {
  Popup,
  NavigationControl,
  Source,
  Layer,
  MapRef,
} from "react-map-gl";
import type { MapMouseEvent, LayerProps } from "react-map-gl";
import { LngLatBounds } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { FeatureCollection, Point } from "geojson";
import { Button } from "@components/ui/button";
import { Skeleton } from "@components/ui/skeleton";
import What3WordsIcon from "@icons/what3words.svg?react";
import { Layers } from "lucide-react";

type TestInfo = {
  id: string;
  latitude: number;
  longitude: number;
  what3words?: string | null;
  name?: string | null;
};

type PopupInfo = TestInfo & {
  popupLatitude: number;
  popupLongitude: number;
  organisationName?: string | null;
  what3words?: string | null;
};

interface SoilTestMapProps {
  locations?: Array<TestInfo> | null;
  loading: boolean;
  excludedOrgs: string[];
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

// Define available map styles
const mapStyles = [
  { name: "Outdoors", url: "mapbox://styles/mapbox/outdoors-v12" },
  { name: "Satellite", url: "mapbox://styles/mapbox/satellite-streets-v12" },
];

const LOCATION_COLOR = "#0ea5e9";

const layerStyle: LayerProps = {
  id: "locations-layer",
  type: "circle",
  paint: {
    "circle-radius": 6,
    "circle-color": LOCATION_COLOR,
    "circle-stroke-width": 1,
    "circle-stroke-color": "#ffffff",
  },
};

export const SoilTestMap = ({
  locations,
  loading,
  excludedOrgs,
}: SoilTestMapProps) => {
  // State to hold the location *and coordinates* for the currently open popup
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
  const mapRef = useRef<MapRef>(null);
  // State for the current map style URL
  const [mapStyleUrl, setMapStyleUrl] = useState(mapStyles[0].url); // Default to Outdoors

  // Filter locations based on excludedOrgs
  const filteredLocations = useMemo(
    () =>
      locations?.filter((location) => !excludedOrgs?.includes(location?.id)) ??
      [],
    [locations, excludedOrgs]
  );

  // Convert filtered locations to GeoJSON FeatureCollection
  const geojson: FeatureCollection<Point, TestInfo> = useMemo(() => {
    return {
      type: "FeatureCollection",
      features: filteredLocations.map((loc) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [loc.longitude, loc.latitude],
        },
        properties: loc,
      })),
    };
  }, [filteredLocations]);

  useEffect(() => {
    if (!mapRef.current || filteredLocations.length === 0) {
      return;
    }

    const map = mapRef.current.getMap();

    if (filteredLocations.length === 1) {
      const [location] = filteredLocations;
      map.flyTo({
        center: [location.longitude, location.latitude],
        zoom: 14,
        essential: true,
      });
    } else {
      const bounds = new LngLatBounds();
      filteredLocations.forEach((loc) => {
        bounds.extend([loc.longitude, loc.latitude]);
      });

      map.fitBounds(bounds, {
        padding: 60,
        duration: 1000,
        essential: true,
      });
    }
  }, [filteredLocations]);

  const toggleMapStyle = () => {
    const currentIndex = mapStyles.findIndex(
      (style) => style.url === mapStyleUrl
    );
    const nextIndex = (currentIndex + 1) % mapStyles.length;
    setMapStyleUrl(mapStyles[nextIndex].url);
  };

  if (!MAPBOX_TOKEN) {
    console.error(
      "Mapbox access token is missing. Map functionality will be limited."
    );
    return <div>Error: Mapbox token not configured.</div>;
  }

  const handleMapClick = (event: MapMouseEvent) => {
    const features = event.features;
    const clickedFeature = features && features[0];

    if (clickedFeature && clickedFeature.layer?.id === layerStyle.id) {
      // Ensure geometry and properties exist
      if (
        clickedFeature.geometry.type === "Point" &&
        clickedFeature.properties
      ) {
        // Extract properties (original location data)
        const properties = clickedFeature.properties as TestInfo;
        // Get coordinates directly from the click event for accurate popup positioning
        const { lng, lat } = event.lngLat;

        setPopupInfo({
          ...properties,
          popupLongitude: lng,
          popupLatitude: lat,
        });
      } else {
        setPopupInfo(null); // Clear popup if data is malformed
      }
    } else {
      // If click was not on a feature of our layer, close any open popup
      setPopupInfo(null);
    }
  };

  return (
    <div className="relative flex h-full flex-col gap-2">
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: -2.5,
          latitude: 54,
          zoom: 5,
        }}
        style={{ flex: 1, minHeight: "200px", borderRadius: "0.6rem" }}
        mapStyle={mapStyleUrl}
        mapboxAccessToken={MAPBOX_TOKEN}
        onClick={handleMapClick}
        interactiveLayerIds={[layerStyle.id as string]}
        cursor={popupInfo ? "auto" : "pointer"}
        logoPosition="bottom-left"
        attributionControl={false}
      >
        <NavigationControl position="top-left" />

        <Button
          onClick={toggleMapStyle}
          variant="secondary"
          size="icon-sm"
          className="absolute right-3 top-3 z-10 rounded-full border bg-background/90 shadow-sm backdrop-blur"
          aria-label="Toggle map style"
          type="button"
        >
          <Layers className="size-4" aria-hidden="true" />
        </Button>

        <Source id="locations-source" type="geojson" data={geojson}>
          <Layer {...layerStyle} />
        </Source>

        {popupInfo && (
          <Popup
            anchor="top"
            longitude={popupInfo.popupLongitude}
            latitude={popupInfo.popupLatitude}
            onClose={() => setPopupInfo(null)}
            closeOnClick={false}
          >
            <div className="space-y-2">
              <div>
                <p className="text-sm font-semibold">{popupInfo.name ?? ""}</p>
                <p className="text-xs text-muted-foreground">
                  {popupInfo.organisationName ?? ""}
                </p>
              </div>
              {popupInfo.what3words && (
                <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                  <What3WordsIcon className="size-4" />
                  <span>{popupInfo.what3words}</span>
                </div>
              )}
              <a
                href={`/admin/pes-tests/${popupInfo.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm font-medium text-primary hover:underline"
              >
                View Test Details
              </a>
            </div>
          </Popup>
        )}
      </Map>
      {loading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-background/60 backdrop-blur">
          <Skeleton className="h-20 w-20 rounded-full" />
        </div>
      )}
    </div>
  );
};
